import {
  ActivatedTitledItem,
  OfferStatusInput,
  Query,
} from '@varo/graphql-schema-types';
import {
  isActivatedOffersPageError,
  isActivatedOffersPageSuccess,
} from 'features/rewards/helpers/ActivatedOffers.helpers';
import { useEffect, useRef, useState } from 'react';
import FetchMyActivatedOffers from 'services/graphql/queries/rewards/FetchMyActivatedOffers';
import { useQuery } from 'urql';

interface LocationCoordinates {
  latitude: number;
  longitude: number;
}

const PAGE_SIZE = 20;

const useFetchMyActivatedOffers = (
  location?: LocationCoordinates | null,
  status?: OfferStatusInput[],
) => {
  const [after, setAfter] = useState<string | null>(null);
  const [activatedItems, setActivatedItems] = useState<ActivatedTitledItem[]>(
    [],
  );
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const fetchingMoreRef = useRef(false);
  const isFirstPageRef = useRef(true);

  const [{ data, fetching: loading, error }, refetch] = useQuery<Query>({
    query: FetchMyActivatedOffers,
    variables: {
      filter: {
        statuses: status,
        location,
      },
      first: PAGE_SIZE,
      after,
    },
    pause: !location,
    requestPolicy: 'cache-and-network',
  });

  const activatedOffersPage = data?.activatedOffersPage;
  const activatedOffersData = isActivatedOffersPageSuccess(activatedOffersPage)
    ? activatedOffersPage
    : undefined;
  const errorData = isActivatedOffersPageError(activatedOffersPage)
    ? activatedOffersPage
    : undefined;

  useEffect(() => {
    setAfter(null);
    setActivatedItems([]);
    isFirstPageRef.current = true;
    fetchingMoreRef.current = false;
    setIsFetchingMore(false);
  }, [location, status]);

  useEffect(() => {
    if (data) {
      const newItems = isActivatedOffersPageSuccess(data?.activatedOffersPage)
        ? (data.activatedOffersPage.activatedItems ?? [])
        : [];
      setActivatedItems((prev) =>
        isFirstPageRef.current ? newItems : [...prev, ...newItems],
      );
      fetchingMoreRef.current = false;
      setIsFetchingMore(false);
    }
  }, [data]);

  const loadMore = () => {
    if (
      fetchingMoreRef.current ||
      !activatedOffersData?.pageInfo?.hasNextPage
    ) {
      return;
    }
    fetchingMoreRef.current = true;
    setIsFetchingMore(true);
    isFirstPageRef.current = false;
    setAfter(activatedOffersData.pageInfo.endCursor ?? null);
  };
  return {
    activatedItems,
    inlineAlertBanner: activatedItems.find((item) => item.inlineAlertBanner)
      ?.inlineAlertBanner,
    loading,
    isPageLoading: isFetchingMore,
    error: errorData ?? error,
    refetch,
    pagination: {
      hasNextPage: activatedOffersData?.pageInfo?.hasNextPage ?? false,
      loadMore,
    },
  };
};

export default useFetchMyActivatedOffers;
